import React, { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form/dist/types/form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import { ApiError } from "go-core/api/types";
import { CustomValidationError } from "go-form/services/types";
import { ApplicationUpdateApi } from "../../../../../../../../../services/Api/Organization/types";
import { AppState } from "../../../utils/types";
import GoOrderAdvancedForm from "./GoOrderAdvancedForm";
import GoOrderMenuErrors from "./GoOrderMenuErrors";
import GoOrderSendingMenuForm from "./GoOrderSendingMenuForm";
import GoOrderShopConfigurationForm from "./GoOrderShopConfigurationForm";
import GoOrderSynchronizationForm from "./GoOrderSynchronizationForm";
import { getDefaultShowAdvancedSectionForGoOrder } from "./utils";

interface Props {
	appState: AppState;
	fetchApplication: () => Promise<void>;
	submitFormTrigger: number;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<ApplicationUpdateApi>[]>>;
	form: UseFormReturn<ApplicationUpdateApi>;
}

const GoOrderForm = ({ appState, fetchApplication, submitFormTrigger, form, setErrors }: Props) => {
	const { t } = useTranslation();
	const [updatedConfig, setUpdatedConfig] = useState<Record<string, any> | undefined>(appState.config);
	const [menuErrors, setMenuErrors] = useState<ApiError[]>([]);
	const {
		register,
		watch,
		formState: { errors },
	} = form;
	const [showAdvancedSection, setShowAdvancedSection] = useState(
		getDefaultShowAdvancedSectionForGoOrder(
			watch().settings.auto_link_by_name,
			watch().settings.external_delivery,
			watch().settings.item_multi_level,
			watch().settings.external_store_id,
			watch().settings.notification_terminal,
			watch().settings.custom_fields_to_sync,
			watch().settings.point_of_sale_id,
			watch().settings.number_of_time_units,
			watch().settings.time_unit,
			watch().settings.order_accept_type,
			watch().settings.image_tag
		)
	);

	useEffect(() => {
		if (submitFormTrigger > 0) {
			setShowAdvancedSection(
				getDefaultShowAdvancedSectionForGoOrder(
					watch("settings").auto_link_by_name,
					watch("settings").external_delivery,
					watch("settings").item_multi_level,
					watch("settings").external_store_id,
					watch("settings").notification_terminal,
					watch("settings").custom_fields_to_sync,
					watch("settings").point_of_sale_id,
					watch("settings").number_of_time_units,
					watch("settings").time_unit,
					watch("settings").order_accept_type,
					watch("settings").image_tag
				)
			);
		}
	}, [submitFormTrigger]);

	const updateConfig = (prop: string, obj?: any) => {
		let newObj;
		if (Array.isArray(obj)) {
			newObj = obj.map((o: Record<string, any>) => {
				return {
					name: o.name,
					id: o.id,
				};
			});
		} else {
			newObj = obj !== undefined ? { ...obj } : obj;
		}

		const cnf = { ...updatedConfig };
		if (prop === "stores") {
			cnf.stores = newObj;
		}
		if (prop === "notification_terminals") {
			cnf.notification_terminals = newObj;
		}
		if (prop === "menu_name") {
			cnf.menu_name = newObj?.label;
		}
		if (prop === "payment_method_name") {
			cnf.payment_method_name = newObj?.label;
		}

		setUpdatedConfig(cnf);
	};

	return (
		<>
			<FormInput label={t("lib:common.word.name")} register={register} name="name" errors={errors} />
			<GoOrderSendingMenuForm
				form={form}
				updatedConfig={updatedConfig}
				setUpdatedConfig={setUpdatedConfig}
				appState={appState}
			/>
			<GoOrderShopConfigurationForm
				form={form}
				updatedConfig={updatedConfig}
				appState={appState}
				updateConfig={updateConfig}
				setErrors={setErrors}
			/>
			<GoOrderSynchronizationForm
				form={form}
				appState={appState}
				fetchApplication={fetchApplication}
				setMenuErrors={setMenuErrors}
			/>
			<GoOrderAdvancedForm
				form={form}
				appState={appState}
				updateConfig={updateConfig}
				updatedConfig={updatedConfig}
				setErrors={setErrors}
				showAdvancedSection={showAdvancedSection}
				setShowAdvancedSection={setShowAdvancedSection}
			/>
			<GoOrderMenuErrors errors={menuErrors} />
		</>
	);
};
export default GoOrderForm;
